import React from "react";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Zap, Fuel, Sun, Grid3x3 } from "lucide-react";

const SOURCE_CONFIG = {
  grid:   { label: "Grid Electricity",    color: "#3B82F6", icon: Grid3x3, unit: "kWh" },
  diesel: { label: "Diesel Generator",    color: "#F59E0B", icon: Fuel,     unit: "kWh" },
  solar:  { label: "Solar / Renewable",   color: "#059669", icon: Sun,      unit: "kWh" },
  other:  { label: "Other Sources",       color: "#94A3B8", icon: Zap,      unit: "kWh" },
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const { name, value } = payload[0];
  const cfg = Object.values(SOURCE_CONFIG).find(c => c.label === name) || {};
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm px-3 py-2 text-xs">
      <p className="font-semibold text-[#0F172A]">{name}</p>
      <p style={{ color: cfg.color }}>{(value / 1000).toFixed(1)} MWh/yr ({(value).toLocaleString()} kWh)</p>
    </div>
  );
};

export default function EnergyMixChart({ breakdown, compact = false }) {
  if (!breakdown) return null;
  const {
    grid_kwh = 0, diesel_kwh = 0, solar_kwh = 0, other_kwh = 0,
    diesel_dependency_pct = 0, total_kwh = 0
  } = breakdown;

  const data = [
    { name: SOURCE_CONFIG.grid.label,   value: grid_kwh,   color: SOURCE_CONFIG.grid.color },
    { name: SOURCE_CONFIG.diesel.label, value: diesel_kwh, color: SOURCE_CONFIG.diesel.color },
    { name: SOURCE_CONFIG.solar.label,  value: solar_kwh,  color: SOURCE_CONFIG.solar.color },
    { name: SOURCE_CONFIG.other.label,  value: other_kwh,  color: SOURCE_CONFIG.other.color },
  ].filter(d => d.value > 0);

  if (data.length === 0) return (
    <div className="flex items-center justify-center h-32 text-xs text-slate-400">
      Enter energy data to see mix chart
    </div>
  );

  const dieselHighRisk = diesel_dependency_pct > 40;
  const dieselMedRisk  = diesel_dependency_pct > 20 && !dieselHighRisk;

  if (compact) {
    return (
      <div className="space-y-2">
        {data.map(d => {
          const pct = Math.round(d.value / Math.max(total_kwh, 1) * 100);
          return (
            <div key={d.name}>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-600">{d.name}</span>
                <span className="font-semibold">{(d.value / 1000).toFixed(1)} MWh ({pct}%)</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: d.color }} />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="energy-mix-chart">
      {/* Diesel dependency KPI */}
      <div className={`flex items-center justify-between px-3 py-2 rounded-lg border text-xs ${
        dieselHighRisk ? "bg-red-50 border-red-200" :
        dieselMedRisk  ? "bg-amber-50 border-amber-200" :
        "bg-emerald-50 border-emerald-200"
      }`}>
        <span className={`font-semibold ${dieselHighRisk ? "text-red-700" : dieselMedRisk ? "text-amber-700" : "text-emerald-700"}`}>
          Diesel Dependency
        </span>
        <span className={`text-lg font-bold tabular-nums ${dieselHighRisk ? "text-red-700" : dieselMedRisk ? "text-amber-700" : "text-emerald-700"}`}>
          {diesel_dependency_pct.toFixed(0)}%
          <span className="text-xs font-normal ml-1">
            {dieselHighRisk ? "High risk" : dieselMedRisk ? "Moderate" : "Low"}
          </span>
        </span>
      </div>

      {/* Pie chart */}
      <ResponsiveContainer width="100%" height={180}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%"
               innerRadius={45} outerRadius={75} paddingAngle={2}>
            {data.map((entry, i) => (
              <Cell key={i} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            iconType="circle"
            iconSize={8}
            formatter={(value) => <span className="text-xs text-slate-600">{value}</span>}
          />
        </PieChart>
      </ResponsiveContainer>

      {/* Source stats */}
      <div className="grid grid-cols-2 gap-2">
        {data.map(d => {
          const pct = Math.round(d.value / Math.max(total_kwh, 1) * 100);
          const cfg = Object.values(SOURCE_CONFIG).find(c => c.label === d.name);
          const Icon = cfg?.icon || Zap;
          return (
            <div key={d.name} className="p-2.5 bg-slate-50 border border-slate-100 rounded-lg">
              <div className="flex items-center gap-1.5 mb-1">
                <Icon size={12} style={{ color: d.color }} />
                <span className="text-xs text-slate-500">{d.name}</span>
              </div>
              <p className="text-sm font-bold tabular-nums" style={{ color: d.color }}>
                {(d.value / 1000).toFixed(1)} MWh
              </p>
              <p className="text-xs text-slate-400">{pct}% of total</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
